import {DifficultySummaries} from '@domain/pom'
import {Button, Grid, H1, useMediaQuery} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import React, {useState, FC, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {
  AddHierarchyNodeDialog,
  ComparativeAnalysisEmptyState,
  HierarchyLevelToggle,
  HierarchyExporter,
  HierarchyNodeCard,
  MAX_COMPARISON_ITEMS,
  DataPointsChart
} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {
  useAllLocationsStats,
  useHierarchyNodeOptions,
  useMonthlyDataPoints,
  useYearlyDataPoints,
  useSelectedNodesQueryParams,
  useChartQueryParams
} from '../hooks'
import {PageContainer} from '../layouts'
import {HierarchyLevel, LocationStats, YearlyHierarchyAnalysisDataPoint} from '../types'
import {
  mergeYearlyWithTodayPoints,
  synthesizeTodayYearlyPoints,
  aggregateMonthlyData,
  aggregateYearlyData
} from '../utils'

const findLocationById = (locations: LocationStats[], id: string): LocationStats | undefined => {
  for (const location of locations) {
    if (location.id === id) return location
    const found = findLocationById(location.subLocations, id)
    if (found) return found
  }
  return undefined
}

const YEARLY_START_YEAR = 2020

const getGridSize = (isMd: boolean, isXl: boolean): 1 | 2 | 3 => {
  if (isMd) return 1
  if (isXl) return 2
  return 3
}

const getOptionalFilterArray = <T,>(value: T | 'all' | undefined): T[] | undefined => {
  if (value === undefined || value === 'all') return undefined
  return [value]
}

const getOptionalSectionIds = (categoryFilter: string[]): string[] | undefined => {
  if (categoryFilter.length === 0) return undefined
  return categoryFilter
}

const getMonthlyToDate = (selectedYear: number, currentYear: number, today: Date): string => {
  if (selectedYear === currentYear) return today.toISOString().split('T')[0]
  return `${selectedYear}-12-31`
}

const isMonthlyDataQueryEnabled = (
  chartViewMode: 'monthly' | 'yearly',
  effectiveNodeIds: string[]
): boolean => {
  return chartViewMode === 'monthly' && effectiveNodeIds.length > 0
}

const isYearlyDataQueryEnabled = (
  chartViewMode: 'monthly' | 'yearly',
  effectiveNodeIds: string[]
): boolean => {
  return chartViewMode === 'yearly' && effectiveNodeIds.length > 0
}

const buildGlobalSummaries = (
  isGlobalMode: boolean,
  locationsStats: LocationStats[]
): DifficultySummaries | undefined => {
  if (!isGlobalMode || locationsStats.length === 0) return undefined

  let basicAP = 0,
    basicQC = 0
  let advancedAP = 0,
    advancedQC = 0
  let excellenceAP = 0,
    excellenceQC = 0

  for (const loc of locationsStats) {
    if (!loc.difficultySummaries) continue

    basicAP += loc.difficultySummaries.basic.answeredPositively
    basicQC += loc.difficultySummaries.basic.questionCount
    advancedAP += loc.difficultySummaries.advanced.answeredPositively
    advancedQC += loc.difficultySummaries.advanced.questionCount
    excellenceAP += loc.difficultySummaries.excellence.answeredPositively
    excellenceQC += loc.difficultySummaries.excellence.questionCount
  }

  return {
    basic: {
      answeredPositively: basicAP,
      questionCount: basicQC,
      positivePercent: basicQC > 0 ? Math.round((basicAP / basicQC) * 100) : 0
    },
    advanced: {
      answeredPositively: advancedAP,
      questionCount: advancedQC,
      positivePercent: advancedQC > 0 ? Math.round((advancedAP / advancedQC) * 100) : 0
    },
    excellence: {
      answeredPositively: excellenceAP,
      questionCount: excellenceQC,
      positivePercent: excellenceQC > 0 ? Math.round((excellenceAP / excellenceQC) * 100) : 0
    }
  }
}

export const ComparativeAnalysisPage: FC = () => {
  const currentYear = new Date().getFullYear()
  const [selectedLevel, setSelectedLevel] = useState<HierarchyLevel>('Plant')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    viewMode: chartViewMode,
    setViewMode: setChartViewMode,
    difficultyFilter,
    setDifficultyFilter,
    categoryFilter,
    setCategoryFilter,
    valueDisplayMode,
    setValueDisplayMode,
    visibleSeriesIds,
    setVisibleSeriesIds,
    selectedYear,
    setSelectedYear
  } = useChartQueryParams()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {selectedNodeIds, setSelectedNodeIds} = useSelectedNodesQueryParams()

  const isGlobalMode = selectedLevel === 'Global'

  // For non-Global levels: options for node selection dialog
  const {data: levelOptions = []} = useHierarchyNodeOptions(isGlobalMode ? 'Area' : selectedLevel)
  // For Global level: all area nodes are used automatically as hierarchy IDs
  const areaIds = useMemo(() => levelOptions.map((o) => String(o.value)), [levelOptions])
  const effectiveNodeIds = isGlobalMode ? areaIds : selectedNodeIds

  const {data: locationsStats = [], isLoading: isLocationsLoading} = useAllLocationsStats()

  const years = useMemo(
    () =>
      Array.from({length: currentYear - YEARLY_START_YEAR + 1}, (_, i) => YEARLY_START_YEAR + i),
    [currentYear]
  )

  const questionDifficulties = getOptionalFilterArray(difficultyFilter)

  const sectionIds = getOptionalSectionIds(categoryFilter)

  const today = new Date()
  // Include Dec of previous year as the starting anchor datapoint for monthly view
  const monthlyFromDate = `${selectedYear - 1}-12-01`
  const monthlyToDate = getMonthlyToDate(selectedYear, currentYear, today)

  const {data: monthlyDataPoints = [], isLoading: isMonthlyLoading} = useMonthlyDataPoints(
    {
      from: monthlyFromDate,
      to: monthlyToDate,
      hierarchyIds: effectiveNodeIds,
      questionDifficulties,
      sectionIds
    },
    {enabled: isMonthlyDataQueryEnabled(chartViewMode, effectiveNodeIds)}
  )
  const {data: yearlyDataPoints = [], isLoading: isYearlyLoading} = useYearlyDataPoints(
    {
      from: YEARLY_START_YEAR,
      to: currentYear,
      hierarchyIds: effectiveNodeIds,
      questionDifficulties,
      sectionIds
    },
    {enabled: isYearlyDataQueryEnabled(chartViewMode, effectiveNodeIds)}
  )

  // Fetch current year monthly data to synthesize a "Today" data point for the yearly chart
  // (the yearly API may only return data for completed years)
  const {data: currentYearMonthlyPoints = [], isLoading: isTodayLoading} = useMonthlyDataPoints(
    {
      from: `${currentYear}-01-01`,
      to: today.toISOString().split('T')[0],
      hierarchyIds: effectiveNodeIds,
      questionDifficulties,
      sectionIds
    },
    {enabled: isYearlyDataQueryEnabled(chartViewMode, effectiveNodeIds)}
  )

  const synthesizedTodayYearlyPoints = useMemo((): YearlyHierarchyAnalysisDataPoint[] => {
    return synthesizeTodayYearlyPoints(currentYearMonthlyPoints, currentYear)
  }, [currentYearMonthlyPoints, currentYear])

  const completedYearlyPoints = useMemo((): YearlyHierarchyAnalysisDataPoint[] => {
    return mergeYearlyWithTodayPoints(yearlyDataPoints, synthesizedTodayYearlyPoints, currentYear)
  }, [yearlyDataPoints, synthesizedTodayYearlyPoints, currentYear])
  const isMd = useMediaQuery({breakpoint: 'md', upTo: true})
  const isXl = useMediaQuery({breakpoint: 'xl', upTo: true})
  const gridSize = getGridSize(isMd, isXl)

  const getNodeSummaries = useCallback(
    (nodeId: string): DifficultySummaries | undefined => {
      const location = findLocationById(locationsStats, nodeId)
      return location?.difficultySummaries
    },
    [locationsStats]
  )

  const handleLevelChange = (level: HierarchyLevel) => {
    setSelectedNodeIds([])
    setSelectedLevel(level)
  }

  const handleRemoveNode = (nodeId: string) => {
    setSelectedNodeIds(selectedNodeIds.filter((id) => id !== nodeId))
  }

  const getNodeName = useCallback(
    (nodeId: string): string => {
      if (nodeId === 'global') return t('comparativeAnalysis.hierarchyLevel.Global')
      const option = levelOptions.find((o) => String(o.value) === nodeId)
      return option?.label ?? nodeId
    },
    [t, levelOptions]
  )

  // Aggregate difficultySummaries from all top-level locations for the Global card
  const globalSummaries = useMemo((): DifficultySummaries | undefined => {
    return buildGlobalSummaries(isGlobalMode, locationsStats)
  }, [isGlobalMode, locationsStats])

  // In Global mode, combine all area data into a single "Global" series
  const effectiveMonthlyData = isGlobalMode
    ? aggregateMonthlyData(monthlyDataPoints)
    : monthlyDataPoints
  const effectiveYearlyData = isGlobalMode
    ? aggregateYearlyData(completedYearlyPoints)
    : completedYearlyPoints

  const showChart = isGlobalMode || selectedNodeIds.length > 0

  return (
    <PageContainer data-test-id="comparative-analysis-page">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6">
        <H1>{t('comparativeAnalysis.title')}</H1>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <HierarchyLevelToggle value={selectedLevel} onChange={handleLevelChange} />
          <HierarchyExporter hierarchyIds={effectiveNodeIds} />
        </div>
      </div>

      {showChart ? (
        <>
          {!isGlobalMode && (
            <Grid size={gridSize} className="gap-4">
              {selectedNodeIds.map((nodeId) => (
                <HierarchyNodeCard
                  key={nodeId}
                  nodeId={nodeId}
                  nodeName={getNodeName(nodeId)}
                  difficultySummaries={getNodeSummaries(nodeId)}
                  isLoading={isLocationsLoading}
                  onRemove={handleRemoveNode}
                />
              ))}
              {selectedNodeIds.length < MAX_COMPARISON_ITEMS && (
                <Button
                  icon={mdiPlus}
                  iconPosition="left"
                  variant="secondary"
                  className="border-2 border-dashed rounded-md border-border_highlight_10 bg-white text-primary h-full min-h-[268px]"
                  data-cy="add-hierarchy-node-card-button"
                  onClick={() => setIsDialogOpen(true)}
                >
                  {t('comparativeAnalysis.addNode', {
                    level: t(`comparativeAnalysis.hierarchyLevel.${selectedLevel}`)
                  })}
                </Button>
              )}
            </Grid>
          )}
          {isGlobalMode && (
            <HierarchyNodeCard
              nodeId="global"
              nodeName={t('comparativeAnalysis.hierarchyLevel.Global')}
              difficultySummaries={globalSummaries}
              isLoading={isLocationsLoading}
            />
          )}
          <DataPointsChart
            monthlyData={effectiveMonthlyData}
            yearlyData={effectiveYearlyData}
            year={selectedYear}
            isLoading={
              chartViewMode === 'monthly' ? isMonthlyLoading : isYearlyLoading || isTodayLoading
            }
            getNodeName={getNodeName}
            viewMode={chartViewMode}
            onViewModeChange={setChartViewMode}
            years={years}
            difficultyFilter={difficultyFilter}
            onDifficultyFilterChange={setDifficultyFilter}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            valueDisplayMode={valueDisplayMode}
            onValueDisplayModeChange={setValueDisplayMode}
            visibleSeriesIds={visibleSeriesIds}
            onVisibleSeriesIdsChange={setVisibleSeriesIds}
            selectedYear={selectedYear}
            onSelectedYearChange={setSelectedYear}
            availableYears={years}
          />
        </>
      ) : (
        <ComparativeAnalysisEmptyState
          selectedLevel={selectedLevel}
          onAdd={() => setIsDialogOpen(true)}
        />
      )}

      {isDialogOpen && !isGlobalMode && (
        <AddHierarchyNodeDialog
          onClose={() => setIsDialogOpen(false)}
          onAdd={(selectedValues) => {
            setSelectedNodeIds(selectedValues.map(String))
            setIsDialogOpen(false)
          }}
          hierarchyLevel={selectedLevel}
          initialSelectedValues={selectedNodeIds}
        />
      )}
    </PageContainer>
  )
}
